import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ADMIN_COLORS as C } from "./adminColors";
import {
  useGenerationAttempt,
  useGenerationAttempts,
} from "hooks/ovizia/useGenerationAttempts";
import {
  fetchGenerationAttempts,
  fetchGenerationAttemptStats,
} from "hooks/ovizia/generationApi";
import Icon from "components/common/Icon";

const STATUS_STYLE = {
  QUEUED: { bg: C.bgSurface, fg: C.textMuted, label: "Queued" },
  RUNNING: { bg: C.blueBg, fg: C.blue, label: "Running" },
  COMPLETED: { bg: C.greenBg, fg: C.green, label: "Completed" },
  FAILED: { bg: C.redBg, fg: C.red, label: "Failed" },
};

function formatDuration(startedAt, completedAt, status) {
  if (!startedAt) return "—";
  if (!completedAt) return status === "RUNNING" ? "In progress…" : "—";
  const ms = new Date(completedAt) - new Date(startedAt);
  const mins = Math.floor(ms / 60000);
  const hrs = Math.floor(mins / 60);
  if (hrs > 0) return `${hrs}h ${mins % 60}m`;
  if (mins > 0) return `${mins}m ${Math.floor((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 1000)}s`;
}

function Pill({ style, children }) {
  return (
    <span
      style={{
        background: style.bg,
        color: style.fg,
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 999,
        width: "max-content",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: "6px 8px",
        borderRadius: 6,
        border: `1px solid ${C.border}`,
        background: "#fff",
        color: C.text,
        cursor: "pointer",
      }}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function StatTile({ label, value, icon, tone }) {
  const fg = tone === "red" ? C.red : tone === "blue" ? C.blue : C.green;
  const bg = tone === "red" ? C.redBg : tone === "blue" ? C.blueBg : C.greenBg;
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: "12px 14px",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={14} style={{ color: fg }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>
          {value}
        </div>
        <div style={{ fontSize: 11, color: C.textMuted }}>{label}</div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  justifyContent: "flex-end",
  zIndex: 9999,
};

const panelStyle = {
  width: 620,
  maxWidth: "94vw",
  height: "100%",
  background: "#fff",
  boxShadow: "-8px 0 24px rgba(0,0,0,0.12)",
  display: "flex",
  flexDirection: "column",
};

function EntityChip({ label, value, onOpen }) {
  if (!value) return null;
  return (
    <button
      onClick={onOpen}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11,
        fontFamily: "monospace",
        fontWeight: 600,
        color: C.textMuted,
        background: C.bgSurface,
        border: `1px solid ${C.border}`,
        borderRadius: 6,
        padding: "3px 8px",
        cursor: "pointer",
      }}
    >
      {label}: {value}
    </button>
  );
}

function AttemptDetailDrawer({ attemptId, onClose, onRetried }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const { attempt, loading, retrying, retry } = useGenerationAttempt(attemptId);

  if (loading || !attempt) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div
          style={{
            ...panelStyle,
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <span style={{ fontSize: 13, color: C.textFaint }}>
            Loading attempt…
          </span>
        </div>
      </div>
    );
  }

  const handleRetry = async () => {
    const newAttempt = await retry();
    onRetried?.(newAttempt);
  };

  const availableTabs = [
    "overview",
    "logs",
    ...(attempt.failureReason ? ["error"] : []),
  ];

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            padding: "18px 22px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: C.textFaint,
                  fontFamily: "monospace",
                }}
              >
                {attempt.id}
              </div>
              <h2
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: C.text,
                  margin: "2px 0 0",
                }}
              >
                {attempt.template} generation
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                color: C.textMuted,
              }}
            >
              <Icon name="x" size={18} />
            </button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 12,
            }}
          >
            <Pill style={STATUS_STYLE[attempt.status]}>
              {STATUS_STYLE[attempt.status].label}
            </Pill>
            <span style={{ fontSize: 12, color: C.textMuted }}>
              Retry #{attempt.retryCount}
              {attempt.previousAttemptId &&
                ` · retried from ${attempt.previousAttemptId}`}
            </span>
            {attempt.status === "FAILED" && (
              <button
                onClick={handleRetry}
                disabled={retrying}
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: C.amber,
                  color: "#fff",
                  cursor: "pointer",
                  opacity: retrying ? 0.6 : 1,
                }}
              >
                {retrying ? "Retrying…" : "Retry generation"}
              </button>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: 4,
              marginTop: 14,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            {availableTabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: "8px 14px",
                  border: "none",
                  borderBottom:
                    tab === t
                      ? `2px solid ${C.green}`
                      : "2px solid transparent",
                  background: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  color: tab === t ? C.green : C.textMuted,
                  textTransform: "capitalize",
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
          {tab === "overview" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.textFaint,
                      textTransform: "uppercase",
                    }}
                  >
                    Started
                  </div>
                  <div style={{ fontSize: 13, color: C.text, marginTop: 2 }}>
                    {attempt.startedAt
                      ? new Date(attempt.startedAt).toLocaleString()
                      : "Not started"}
                  </div>
                </div>
                <div
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.textFaint,
                      textTransform: "uppercase",
                    }}
                  >
                    Duration
                  </div>
                  <div style={{ fontSize: 13, color: C.text, marginTop: 2 }}>
                    {formatDuration(
                      attempt.startedAt,
                      attempt.completedAt,
                      attempt.status,
                    )}
                  </div>
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.textFaint,
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Linked entities
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <EntityChip
                    label="Website"
                    value={attempt.websiteId}
                    onOpen={() => navigate("/admin/websites")}
                  />
                  <EntityChip
                    label="Workspace"
                    value={attempt.workspaceId}
                    onOpen={() => navigate("/admin/workspaces")}
                  />
                  <EntityChip
                    label="User"
                    value={attempt.userId}
                    onOpen={() => navigate("/admin/users")}
                  />
                  {attempt.errorIds?.map((errId) => (
                    <EntityChip
                      key={errId}
                      label="Error"
                      value={errId}
                      onOpen={() => navigate("/admin/timeline")}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "error" && (
            <div
              style={{
                border: "1px solid rgba(220,38,38,0.25)",
                background: C.redBg,
                borderRadius: 10,
                padding: "14px 16px",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: C.red,
                  marginBottom: 6,
                }}
              >
                Failure reason
              </div>
              <div style={{ fontSize: 13, color: C.text }}>
                {attempt.failureReason}
              </div>
            </div>
          )}

          {tab === "logs" && (
            <pre
              style={{
                background: "#1a1a1a",
                color: "#d4d4d4",
                fontSize: 12,
                lineHeight: 1.8,
                padding: 14,
                borderRadius: 8,
                overflowX: "auto",
                fontFamily: "monospace",
              }}
            >
              {attempt.logs.map((l, i) => (
                <div
                  key={i}
                  style={{ color: l.level === "error" ? "#f87171" : "#d4d4d4" }}
                >
                  [{l.level}] {l.message}
                </div>
              ))}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function CompareModal({ attempts, onClose }) {
  const fields = [
    { key: "id", label: "Attempt ID" },
    { key: "websiteId", label: "Website" },
    { key: "template", label: "Template" },
    {
      key: "status",
      label: "Status",
      render: (v) => STATUS_STYLE[v]?.label || v,
    },
    {
      key: "startedAt",
      label: "Started",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      key: "duration",
      label: "Duration",
      render: (_, a) => formatDuration(a.startedAt, a.completedAt, a.status),
    },
    { key: "retryCount", label: "Retry count" },
    { key: "failureReason", label: "Failure reason", render: (v) => v || "—" },
  ];

  return (
    <div
      style={{
        ...overlayStyle,
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 720,
          maxWidth: "94vw",
          maxHeight: "85vh",
          overflowY: "auto",
          background: "#fff",
          borderRadius: 12,
          padding: 22,
          boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h3
            style={{ fontSize: 15, fontWeight: 800, color: C.text, margin: 0 }}
          >
            Compare attempts
          </h3>
          <button
            onClick={onClose}
            style={{ border: "none", background: "none", cursor: "pointer" }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
        >
          <tbody>
            {fields.map((f) => (
              <tr key={f.key} style={{ borderTop: `1px solid ${C.border}` }}>
                <td
                  style={{
                    padding: "8px 10px",
                    fontWeight: 700,
                    color: C.textFaint,
                    width: 130,
                    verticalAlign: "top",
                  }}
                >
                  {f.label}
                </td>
                {attempts.map((a) => (
                  <td
                    key={a.id}
                    style={{
                      padding: "8px 10px",
                      color: C.text,
                      verticalAlign: "top",
                    }}
                  >
                    {f.render ? f.render(a[f.key], a) : a[f.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const GRID_COLS = "20px 1.2fr 0.8fr 0.9fr 1.1fr 0.9fr 0.7fr 20px";

export default function GenerationMonitoringPage() {
  const {
    attempts,
    templates,
    loading,
    query,
    setQuery,
    status,
    setStatus,
    template,
    setTemplate,
    refresh,
  } = useGenerationAttempts();
  const [selectedId, setSelectedId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [globalCounts, setGlobalCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      const counts = await fetchGenerationAttemptStats();
      if (cancelled) return;
      setGlobalCounts(counts);
    };
    loadCounts();
    const interval = setInterval(loadCounts, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [attempts]);

  const toggleCompare = (id, e) => {
    e.stopPropagation();
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const compareAttempts = attempts.filter((a) => compareIds.includes(a.id));

  return (
    <div
      style={{
        padding: "24px 28px",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 20,
          flexShrink: 0,
        }}
      >
        <div>
          <h1
            style={{ fontSize: 20, fontWeight: 800, color: C.green, margin: 0 }}
          >
            Generation monitoring
          </h1>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>
            Every generation attempt, its status, and why it failed.
          </p>
        </div>

        <button
          onClick={refresh}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: C.textMuted,
            border: `1px solid ${C.border}`,
            borderRadius: 7,
            background: "#fff",
            padding: "7px 12px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Icon name="refresh" size={13} /> Refresh
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 10,
          marginBottom: 20,
        }}
      >
        <StatTile
          label="Running now"
          value={globalCounts?.running ?? "—"}
          icon="loader-2"
          tone="blue"
        />
        <StatTile
          label="Queued"
          value={globalCounts?.queued ?? "—"}
          icon="clock"
          tone="blue"
        />
        <StatTile
          label="Failed"
          value={globalCounts?.failed ?? "—"}
          icon="alert-triangle"
          tone="red"
        />
        <StatTile
          label="Completed"
          value={globalCounts?.completed ?? "—"}
          icon="circle-check"
          tone="green"
        />
      </div>

      <div
        style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 220,
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: "9px 12px",
            background: "#fff",
          }}
        >
          <Icon name="search" size={15} style={{ color: C.textFaint }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by attempt ID, website, workspace, user, or template..."
            style={{
              border: "none",
              outline: "none",
              flex: 1,
              fontSize: 13,
              fontFamily: "inherit",
            }}
          />
        </div>
        <Select
          value={status}
          onChange={setStatus}
          options={[
            { value: "", label: "All statuses" },
            ...Object.keys(STATUS_STYLE).map((s) => ({
              value: s,
              label: STATUS_STYLE[s].label,
            })),
          ]}
        />
        <Select
          value={template}
          onChange={setTemplate}
          options={[
            { value: "", label: "All templates" },
            ...templates.map((t) => ({ value: t, label: t })),
          ]}
        />
      </div>

      <div
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: GRID_COLS,
            padding: "9px 16px",
            background: C.bgSurface,
            fontSize: 10,
            fontWeight: 700,
            color: C.textFaint,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          <span />
          <span>Attempt</span>
          <span>Template</span>
          <span>Status</span>
          <span>Started</span>
          <span>Duration</span>
          <span>Retries</span>
          <span />
        </div>
        {loading ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: C.textFaint,
              fontSize: 13,
            }}
          >
            Loading attempts...
          </div>
        ) : attempts.length === 0 ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: C.textFaint,
              fontSize: 13,
            }}
          >
            No attempts match.
          </div>
        ) : (
          attempts.map((a) => (
            <div
              key={a.id}
              onClick={() => setSelectedId(a.id)}
              style={{
                display: "grid",
                gridTemplateColumns: GRID_COLS,
                padding: "12px 16px",
                borderTop: `1px solid ${C.border}`,
                cursor: "pointer",
                alignItems: "center",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = C.bgSurface)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <input
                type="checkbox"
                checked={compareIds.includes(a.id)}
                onChange={() => {}}
                onClick={(e) => toggleCompare(a.id, e)}
                style={{ cursor: "pointer" }}
              />
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.text,
                    fontFamily: "monospace",
                  }}
                >
                  {a.id}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: C.textFaint,
                    fontFamily: "monospace",
                  }}
                >
                  {a.websiteId}
                </div>
              </div>
              <div style={{ fontSize: 12, color: C.textMuted }}>
                {a.template}
              </div>
              <Pill style={STATUS_STYLE[a.status]}>
                {STATUS_STYLE[a.status].label}
              </Pill>
              <div style={{ fontSize: 12, color: C.textFaint }}>
                {a.startedAt
                  ? new Date(a.startedAt).toLocaleString()
                  : "Not started"}
              </div>
              <div style={{ fontSize: 12, color: C.textMuted }}>
                {formatDuration(a.startedAt, a.completedAt, a.status)}
              </div>
              <div style={{ fontSize: 12, color: C.textMuted }}>
                {a.retryCount}
              </div>
              <Icon
                name="chevron-right"
                size={15}
                style={{ color: C.textFaint }}
              />
            </div>
          ))
        )}
      </div>

      {selectedId && (
        <AttemptDetailDrawer
          attemptId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

      {compareIds.length === 2 && !selectedId && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            background: C.text,
            color: "#fff",
            borderRadius: 999,
            padding: "10px 10px 10px 18px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            zIndex: 500,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            2 attempts selected
          </span>
          <button
            onClick={() => setComparing(true)}
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: "6px 12px",
              borderRadius: 999,
              border: "none",
              background: "#fff",
              color: C.text,
              cursor: "pointer",
            }}
          >
            Compare
          </button>
          <button
            onClick={() => setCompareIds([])}
            style={{
              border: "none",
              background: "none",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
            }}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      )}

      {comparing && compareAttempts.length === 2 && (
        <CompareModal
          attempts={compareAttempts}
          onClose={() => setComparing(false)}
        />
      )}
    </div>
  );
}
